import React from 'react';
import useAuthStore from '../store/authStore';
import useUserOnlineStatus from '../hooks/useUserOnlineStatus';
import { getImageUrl } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';

export default function ProductDetail({ conversation, isMobile = false, onClose }) {
  const { isDarkMode } = useDarkMode();
  const { user } = useAuthStore();
  const currentUserEmail = user?.email;
  const currentUserUid = user?.uid;

  // Fallback sample data for buyer (for the Buyer Section Modern UI)
  const buyer = conversation || {
    name: 'Rose Stott',
    avatar: 'https://i.pravatar.cc/60?img=12',
    online: false,
  };

  // Use product details from the conversation object, fallback to sample if missing
  const product = {
    name: conversation?.product_name || 'Product',
    price: conversation?.product_price || 'N/A',
    status: conversation?.product_status || 'Available',
    image: conversation?.product_image ? getImageUrl(conversation.product_image) : 'https://img.icons8.com/color/96/000000/youtube-play.png',
    features: conversation?.product_features || ['Monetized', 'Verified', 'Fast Delivery'],
    description: conversation?.product_description || '',
    category: conversation?.product_category || '',
    seller: {
      name: conversation?.seller?.name || conversation?.seller_name || 'Seller',
      avatar: conversation?.seller?.avatar || conversation?.seller_avatar || 'https://i.pravatar.cc/40?img=1',
      rating: conversation?.seller?.rating || 4.9,
      reviews: conversation?.seller?.reviews || 128,
      verified: typeof conversation?.seller?.verified !== 'undefined' ? conversation.seller.verified : true,
      email: conversation?.seller?.email || '',
      uid: conversation?.seller?.uid || '',
      username: conversation?.seller?.username || '',
    },
    buyer: {
      name: conversation?.buyer?.name || conversation?.buyer_name || 'Buyer',
      avatar: conversation?.buyer?.avatar || conversation?.buyer_avatar || 'https://i.pravatar.cc/40?img=2',
      rating: conversation?.buyer?.rating || 4.9,
      reviews: conversation?.buyer?.reviews || 128,
      verified: typeof conversation?.buyer?.verified !== 'undefined' ? conversation.buyer.verified : true,
      email: conversation?.buyer?.email || '',
      uid: conversation?.buyer?.uid || '',
      username: conversation?.buyer?.username || '',
    },
    product_id: conversation?.product_id || '',
    url: conversation?.product_id ? `/product/${conversation.product_id}` : '#',
  };

  // Determine which party to show (other than current user)
  let showSeller = true;
  
  // Check if current user is the seller
  const isCurrentUserSeller = (
    (product.seller.uid && currentUserUid && product.seller.uid === currentUserUid) ||
    (product.seller.email && currentUserEmail && product.seller.email.toLowerCase() === currentUserEmail.toLowerCase())
  );
  
  // Check if current user is the buyer
  const isCurrentUserBuyer = (
    (product.buyer.uid && currentUserUid && product.buyer.uid === currentUserUid) ||
    (product.buyer.email && currentUserEmail && product.buyer.email.toLowerCase() === currentUserEmail.toLowerCase())
  );
  
  if (isCurrentUserSeller) {
    // Current user is the seller, show buyer info
    showSeller = false;
  } else if (isCurrentUserBuyer) {
    // Current user is the buyer, show seller info
    showSeller = true;
  }
  // If neither, default to showing seller (fallback)
  
  // Debug logging to help identify the issue
  if (process.env.NODE_ENV === 'development' && false) {
    console.log('ProductDetail Debug:', {
      currentUserEmail,
      currentUserUid,
      sellerEmail: product.seller.email,
      sellerUid: product.seller.uid,
      buyerEmail: product.buyer.email,
      buyerUid: product.buyer.uid,
      isCurrentUserSeller,
      isCurrentUserBuyer,
      showSeller,
      cardTitle: showSeller ? 'Seller' : 'Buyer'
    });
  }

  let cardTitle = showSeller ? 'Seller' : 'Buyer';
  let cardName = showSeller
    ? (product.seller.name || 'Seller')
    : (product.buyer.name || 'Buyer');
  let cardAvatar = showSeller
    ? (product.seller.avatar)
    : (product.buyer.avatar);
  // If no avatar, use ui-avatars.com with initials
  if (!cardAvatar || cardAvatar.trim() === '' || cardAvatar.includes('pravatar.cc')) {
    cardAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(cardName)}&background=dddddd&color=222222&rounded=true&size=64`;
  }
  let cardUsername = showSeller
    ? (product.seller.username || '')
    : (product.buyer.username || '');
  let cardEmail = showSeller
    ? (product.seller.email || '')
    : (product.buyer.email || '');
  let cardUid = showSeller
    ? (product.seller.uid || '')
    : (product.buyer.uid || '');
  let profileUrl = showSeller
    ? (conversation?.seller_username ? `/profile/${conversation.seller_username}` : '#')
    : (conversation?.buyer_username ? `/profile/${conversation.buyer_username}` : '#');
  const cardOnline = useUserOnlineStatus(cardUid);

  return (
    <aside className={`${isMobile ? 'fixed inset-0 z-50 w-full' : 'w-[380px]'} ${
      isDarkMode 
        ? 'bg-gradient-to-b from-[#0f0f0f] to-[#0f0f0f] border-l border-gray-700/50 shadow-2xl shadow-black/50' 
        : 'bg-gradient-to-b from-white to-gray-50 border-l border-gray-200 shadow-lg'
    } flex flex-col h-full p-0`}>
      <div className="px-6 pt-8 pb-6">
                            <div className={`backdrop-blur rounded-2xl px-6 py-3 font-bold text-lg shadow-lg mb-6 flex items-center justify-center relative ${
                              isDarkMode 
                                ? 'bg-[#2a2a2a] border border-gray-700/50 text-white' 
                                : 'bg-white text-gray-900'
                            }`}>
            Product Detail
          {/* Close button for mobile */}
          {isMobile && onClose && (
            <button 
              onClick={onClose}
              className={`absolute right-4 p-2 rounded-full transition-colors ${
                isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'
              }`}
              aria-label="Close product details"
            >
              <svg className={`w-6 h-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {/* Product Card Modern UI */}
        <div className={`rounded-2xl shadow-lg flex flex-col items-center p-0 mb-8 overflow-hidden ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#2a2a2a] via-[#2a2a2a] to-[#2a2a2a] border border-gray-700/50' 
            : 'bg-white'
        }`}>
          {/* Product Gallery */}
                      <div className={`relative w-full h-48 flex items-center justify-center ${
            isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
          }`}>
            <a href={product.url} target="_blank" rel="noopener noreferrer">
              <img src={product.image} alt="Product Thumbnail" style={{ width: '329px', height: '192px', objectFit: 'cover' }} className="rounded-lg shadow" />
            </a>
            <span className="absolute top-4 left-4 bg-green-500 text-white text-xs px-3 py-1 rounded-full shadow font-semibold">{product.status}</span>
            <span className="absolute top-4 right-4 bg-red-500 text-white text-xs px-3 py-1 rounded-full shadow font-semibold">${product.price}</span>
          </div>
          {/* Product Info */}
          <div className="p-6 w-full">
            <a href={product.url} target="_blank" rel="noopener noreferrer">
                              <div className={`font-bold text-lg mb-1 truncate w-full hover:underline cursor-pointer ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`} title={product.name}>{product.name}</div>
            </a>
            <div className={`text-sm mb-2 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>{product.description}</div>
            <div className={`text-xs mb-4 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>{product.category}</div>
            {/* Seller/Agent Card removed as requested */}
            <a href={product.url} target="_blank" rel="noopener noreferrer">
              <button className="w-full mt-2 px-8 bg-red-500 hover:bg-red-600 text-white font-bold py-2 rounded-lg shadow-lg transition">View Product</button>
            </a>
          </div>
        </div>
        {/* Buyer/Seller Section Modern UI */}
        <div className={`rounded-2xl shadow-lg p-6 flex flex-col items-center mt-4 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#2a2a2a] via-[#2a2a2a] to-[#2a2a2a] border border-gray-700/50' 
            : 'bg-white'
        }`}>
                      <div className={`font-bold text-lg mb-4 w-full text-left ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {cardTitle}
          </div>
          <div className="flex flex-col items-center w-full">
            <div className="flex items-center gap-3 mb-1">
              {profileUrl !== '#' ? (
                <a
                  href={profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  style={{ display: 'inline-block' }}
                >
                  <div className="relative">
                    <img
                      src={cardAvatar}
                      alt={cardName}
                      className={`w-16 h-16 rounded-full border-4 border-white shadow group-hover:opacity-90 ${
                        isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}
                    />
                    <span className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white ${cardOnline ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                  </div>
                </a>
              ) : (
                <div className="relative">
                  <img
                    src={cardAvatar}
                    alt={cardName}
                    className={`w-16 h-16 rounded-full border-4 border-white shadow ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                    }`}
                  />
                  <span className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white ${cardOnline ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                </div>
              )}
              <div>
                <div className={`font-semibold text-base flex items-center gap-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {/* Show the other party's name as a profile link with underline on hover */}
                  {profileUrl !== '#' ? (
                    <a
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      {cardName}
                    </a>
                  ) : (
                    <span>{cardName}</span>
                  )}
                  {/* Verified badge: show only for seller */}
                  {cardTitle === 'Seller' && (
                    <span style={{
                      background: '#E6FAEA',
                      color: '#1DBF73',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      borderRadius: '8px',
                      padding: '2px 10px',
                      marginLeft: '8px'
                    }}>
                      Verified
                    </span>
                  )}
                </div>
                {/* Online status */}
                <div className={`text-sm ${cardOnline ? 'text-green-600' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>{cardOnline ? 'Online' : 'Offline'}</div>
                {/* Star rating and review count: show for both seller and buyer */}
                <div className={`flex items-center gap-1 text-sm mt-1 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><polygon points="10,1 12,7 18,7 13,11 15,17 10,13 5,17 7,11 2,7 8,7" /></svg>
                  {(cardTitle === 'Seller'
                    ? (product.seller.rating)
                    : (product.buyer.rating))}
                  <span className={`text-xs ml-1 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>(
                    {(cardTitle === 'Seller'
                      ? (product.seller.reviews)
                      : (product.buyer.reviews))} reviews)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
} 